// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Local-only implementation of useRecords backed by RxDB.
 *
 * Queries and mutates virtual collections via SchemaRegistry and
 * VirtualCollectionAdapter. Extracted from useRecords for mode routing.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { SchemaRegistry } from '../../../core/SchemaRegistry'
import { VirtualCollectionAdapter, wrapRxCollection } from '../../../core/VirtualCollectionAdapter'
import type { RecordModel } from '../../../models/Record'

export interface UseLocalRecordsOptions {
  selector?: Record<string, unknown>
  sort?: Array<Record<string, string>>
  limit?: number
  skip?: number
  orgId?: string
  workspaceId?: string
}

export interface UseLocalRecordsResult<T = Record<string, unknown>> {
  data: T[]
  isLoading: boolean
  error: Error | null
  create: (doc: Partial<T>) => Promise<T>
  update: (id: string, updates: Partial<T>) => Promise<T | null>
  remove: (id: string) => Promise<boolean>
  refresh: () => void
}

// Module-level cache: one SchemaRegistry per db instance
const registryCache = new WeakMap<object, SchemaRegistry>()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getOrCreateRegistry(db: Record<string, any>): SchemaRegistry {
  let registry = registryCache.get(db as object)
  if (!registry) {
    registry = new SchemaRegistry(
      db.schemas as ConstructorParameters<typeof SchemaRegistry>[0],
      wrapRxCollection(db.records)
    )
    registryCache.set(db as object, registry)
  }
  return registry
}

const EMPTY_DATA: never[] = []
const NOOP_CREATE = async (): Promise<never> => {
  throw new Error('useLocalRecords: not initialized')
}
const NOOP_UPDATE = async (): Promise<null> => {
  throw new Error('useLocalRecords: not initialized')
}
const NOOP_REMOVE = async (): Promise<boolean> => {
  throw new Error('useLocalRecords: not initialized')
}
const NOOP_REFRESH = (): void => {}

/**
 * Generic React hook for querying and mutating virtual collections
 * backed by the `records` table via `SchemaRegistry` and `VirtualCollectionAdapter`
 * (local RxDB path only).
 *
 * @param db - RxDB database instance (must have `schemas` and `records` collections)
 * @param schemaName - Name of the virtual collection schema (e.g. 'products', 'codebases')
 * @param options - Query options: selector, sort, limit, skip, orgId
 * @returns Reactive data array, loading/error state, and CRUD mutations
 */
export function useLocalRecords<T = Record<string, unknown>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: Record<string, any> | null | undefined,
  schemaName: string,
  options: UseLocalRecordsOptions = {}
): UseLocalRecordsResult<T> {
  const [data, setData] = useState<T[]>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(db != null)
  const [error, setError] = useState<Error | null>(null)
  const adapterRef = useRef<VirtualCollectionAdapter | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Serialize options for dependency comparison
  const selectorKey = JSON.stringify(options.selector ?? {})
  const sortKey = JSON.stringify(options.sort ?? [])
  const { limit, skip, orgId, workspaceId } = options

  useEffect(() => {
    if (!db) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset to defaults when db removed
      setData(EMPTY_DATA)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoading(false)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(null)
      return
    }

    let cancelled = false
    let subscription: { unsubscribe: () => void } | null = null

    async function init() {
      try {
        setIsLoading(true)
        setError(null)

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const registry = getOrCreateRegistry(db!)

        // Load schemas if registry doesn't have this schema yet
        if (!registry.hasSchema(schemaName)) {
          await registry.loadSchemas(orgId ?? '')
        }

        // If schema still not found, retry once after a short delay.
        // This handles the race condition where useRecords runs before
        // schema seeding (triggered on app startup) has completed.
        if (!registry.hasSchema(schemaName)) {
          await new Promise(resolve => setTimeout(resolve, 1500))
          if (cancelled) return
          await registry.loadSchemas(orgId ?? '')
        }

        const adapter = registry.getAdapter(schemaName)
        if (!adapter) {
          if (!cancelled) {
            setError(new Error(`Schema '${schemaName}' not found or not a records-target schema`))
            setIsLoading(false)
            setData(EMPTY_DATA)
          }
          return
        }

        adapterRef.current = adapter

        // Build the RxDB query — only top-level fields go to RxDB,
        // data-bag fields are filtered client-side after mapping.
        // Parse from serialized keys so the linter sees the deps match.
        const selector = JSON.parse(selectorKey) as Record<string, unknown>

        // Auto-inject org/workspace context into the selector so queries
        // are scoped to the current tenant. Callers pass orgId/workspaceId
        // in options; we merge them here so every consumer gets filtering
        // without needing to duplicate context in both options and selector.
        if (orgId && !selector.orgId) selector.orgId = orgId
        if (workspaceId && !selector.workspaceId) selector.workspaceId = workspaceId

        const rewrittenSelector = adapter.rewriteSelector(selector)
        const dataFilter = adapter.extractDataFilter(selector)
        const hasDataFilter = Object.keys(dataFilter).length > 0

        // Split sort into RxDB-safe and client-side parts
        const sortOpts = JSON.parse(sortKey) as Array<Record<string, 'asc' | 'desc'>>
        const dbSort = adapter.rewriteSort(sortOpts)
        const clientSort = adapter.extractClientSort(sortOpts)

        const records = (db as Record<string, unknown>).records as {
          find(q: { selector: Record<string, unknown>; sort?: Array<Record<string, string>> }): {
            skip(n: number): unknown
            limit(n: number): unknown
            $: { subscribe(fn: (docs: unknown[]) => void): { unsubscribe: () => void } }
          }
        }

        // Pass sort in find() options — RxDB's query builder .sort() method
        // does not handle array-of-objects format correctly (it enters the
        // array-of-tuples branch, producing {"0": "desc"} sort fields).
        const findOptions: {
          selector: Record<string, unknown>
          sort?: Array<Record<string, string>>
        } = { selector: rewrittenSelector }
        if (dbSort.length > 0) {
          findOptions.sort = dbSort
        }

        let query = records.find(findOptions) as {
          skip(n: number): unknown
          limit(n: number): unknown
          $: { subscribe(fn: (docs: unknown[]) => void): { unsubscribe: () => void } }
        }
        // When client-side filtering is needed, skip/limit must be applied after filtering
        if (skip && !hasDataFilter) query = query.skip(skip) as typeof query
        if (limit && !hasDataFilter) query = query.limit(limit) as typeof query

        // Subscribe for live updates
        subscription = query.$.subscribe((docs: unknown[]) => {
          if (cancelled) return
          let mapped = docs.map(
            doc =>
              adapter.mapFromRecord(
                (doc as { toJSON(): Record<string, unknown> }).toJSON() as unknown as RecordModel
              ) as unknown as T
          )

          // Apply client-side data filter for data-bag fields
          if (hasDataFilter) {
            mapped = mapped.filter(doc =>
              VirtualCollectionAdapter.matchesDataFilter(
                doc as unknown as Record<string, unknown>,
                dataFilter
              )
            )
          }

          // Apply client-side sort for data-bag fields
          if (clientSort.length > 0) {
            mapped = VirtualCollectionAdapter.applyClientSort(
              mapped as unknown as Record<string, unknown>[],
              clientSort
            ) as unknown as T[]
          }

          // Apply skip/limit after client-side processing if needed
          if (hasDataFilter) {
            if (skip) mapped = mapped.slice(skip)
            if (limit) mapped = mapped.slice(0, limit)
          }

          setData(mapped)
          setIsLoading(false)
        })
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
        }
      }
    }

    init()

    return () => {
      cancelled = true
      subscription?.unsubscribe()
    }
  }, [db, schemaName, selectorKey, sortKey, limit, skip, orgId, workspaceId, refreshTrigger])

  const create = useCallback(async (doc: Partial<T>): Promise<T> => {
    const adapter = adapterRef.current
    if (!adapter) throw new Error('useLocalRecords: adapter not initialized')
    const result = await adapter.insert(doc as Record<string, unknown>)
    return result as unknown as T
  }, [])

  const update = useCallback(async (id: string, updates: Partial<T>): Promise<T | null> => {
    const adapter = adapterRef.current
    if (!adapter) throw new Error('useLocalRecords: adapter not initialized')
    const result = await adapter.update(id, updates as Record<string, unknown>)
    return result as unknown as T | null
  }, [])

  const remove = useCallback(async (id: string): Promise<boolean> => {
    const adapter = adapterRef.current
    if (!adapter) throw new Error('useLocalRecords: adapter not initialized')
    return adapter.remove(id)
  }, [])

  const refresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1)
  }, [])

  if (!db) {
    return {
      data: EMPTY_DATA as T[],
      isLoading: false,
      error: null,
      create: NOOP_CREATE as unknown as UseLocalRecordsResult<T>['create'],
      update: NOOP_UPDATE as unknown as UseLocalRecordsResult<T>['update'],
      remove: NOOP_REMOVE as unknown as UseLocalRecordsResult<T>['remove'],
      refresh: NOOP_REFRESH,
    }
  }

  return { data, isLoading, error, create, update, remove, refresh }
}
